/**
 * Axios instance with base configuration and request/response interceptors.
 *
 * All API calls in endpoints.ts use this instance — never import axios directly
 * in components or hooks.
 */

import axios from "axios";
import type { ErrorResponse } from "./types";

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api/v1",
  timeout: 120_000,
});

/** Typed error thrown by all failed API calls. */
export class ApiError extends Error {
  constructor(
    public readonly error_code: string,
    message: string,
    public readonly status: number,
    public readonly detail: Record<string, unknown> | null = null
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Response interceptor: normalize error shapes from the backend
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const data = error.response?.data as ErrorResponse | undefined;

      if (data?.error_code) {
        return Promise.reject(
          new ApiError(data.error_code, data.message, status, data.detail)
        );
      }

      return Promise.reject(
        new ApiError("NETWORK_ERROR", error.message, status)
      );
    }

    return Promise.reject(error);
  }
);
